import { useState } from "react";
import { useCoupons } from "@/hooks/useCoupons";
import CouponCard from "./CouponCard";
import AddCouponDialog from "./AddCouponDialog";
import { Tag, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CouponManagementProps {
  providerId: string;
  services: Array<{ id: string; name: string; }>;
}

const CouponManagement = ({ providerId, services }: CouponManagementProps) => {
  const [showDeleted, setShowDeleted] = useState(false);
  const { data: coupons, isLoading, refetch } = useCoupons(providerId);

  console.log('CouponManagement rendering with coupons:', coupons);

  const handleCouponUpdate = () => {
    console.log('Refetching coupons...');
    refetch();
  };

  // Separate active and deleted coupons for display
  const activeCoupons = coupons?.filter(c => !c.deleted_at) || [];
  const deletedCoupons = coupons?.filter(c => c.deleted_at) || [];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-64 bg-gray-200 rounded animate-pulse"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toggle button for showing/hiding deleted coupons */}
      {(activeCoupons.length > 0 || deletedCoupons.length > 0) && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleted(!showDeleted)}
            className="gap-2"
          >
            {showDeleted ? (
              <>
                <EyeOff className="h-4 w-4" />
                Hide Deleted
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                Show Deleted ({deletedCoupons.length})
              </>
            )}
          </Button>
        </div>
      )}

      {/* Display coupons */}
      {coupons && coupons.length > 0 ? (
        <div className="space-y-6">
          {/* Active coupons */}
          {activeCoupons.length > 0 && (
            <div>
              {showDeleted && <h3 className="text-sm font-medium text-muted-foreground mb-3">Active Coupons</h3>}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeCoupons.map((coupon) => (
                  <CouponCard 
                    key={coupon.id} 
                    coupon={coupon} 
                    services={services}
                    onCouponUpdated={handleCouponUpdate}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Deleted coupons - only show when toggle is on */}
          {showDeleted && deletedCoupons.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Deleted Coupons</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {deletedCoupons.map((coupon) => (
                  <CouponCard 
                    key={coupon.id} 
                    coupon={coupon} 
                    services={services}
                    onCouponUpdated={handleCouponUpdate}
                    isDeleted={true}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No coupons created yet</p>
          {services.length > 0 ? (
            <div className="mt-4">
              <AddCouponDialog
                providerId={providerId}
                services={services}
                onCouponAdded={handleCouponUpdate}
              />
            </div>
          ) : (
            <p className="text-sm mt-2 opacity-70">
              Create services first to add coupons
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CouponManagement;
